/**
 * The tenant's product marks (TMMAPP-0035), following saas-auth's asset fallback chain exactly.
 *
 * - `BrandLogo` on a light surface: `logo`, else `fallback`.
 * - `BrandLogo reversed` (dark mode, or on a `brand` / `appBar` / `brandDeep` fill):
 *   `logoReversed`, else the `logo` on a light chip, else `fallback`.
 * - `fallback`: the `productShortName` wordmark (default), or `BrandMark`.
 * - `BrandMark`: `mark`, else `favicon.png`, else the initial of `productShortName` on `brand`. With
 *   `bundledFallback` (the v2 wordmark, TMMAPP-0151 §A 6): `mark`, else the app's bundled mark,
 *   never a typed initial.
 *
 * Images carry `alt={productName}` (the mark: `brand.logoAlt`); an image that fails to load
 * falls to the next step of its chain.
 */
import { useState, type ReactNode } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { resolveTokens } from '@/theme/theme';
import { APP_IDENTITY_ASSETS, useBrand } from '@/theme/useBrand';

function useFailedImages() {
  const [failed, setFailed] = useState<ReadonlySet<string>>(() => new Set());
  const usable = (src: string | null): src is string => src !== null && !failed.has(src);
  const onError = (src: string) => () => setFailed((prev) => new Set(prev).add(src));
  return { usable, onError };
}

const BUNDLED_MARK_REVERSED = '/brand/money-meeting-mark-reversed.svg';

export function BrandMark({
  size = 36,
  bundledFallback = false,
}: {
  size?: number;
  bundledFallback?: boolean;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const { assets, productName, productShortName } = useBrand();
  const { usable, onError } = useFailedImages();
  const alt = t('brand.logoAlt', { productName });
  // The bundled mark's reversed twin on a dark canvas (TMMAPP-0151 §A 6).
  const bundled = theme.palette.mode === 'dark' ? BUNDLED_MARK_REVERSED : APP_IDENTITY_ASSETS.mark;
  const src = bundledFallback
    ? ([assets.mark, bundled].find(usable) ?? bundled)
    : [assets.mark, assets.favicon.png].find(usable);
  if (src !== undefined && src !== null) {
    return (
      <Box
        component="img"
        src={src}
        alt={alt}
        onError={onError(src)}
        sx={{ height: size, width: size, display: 'block' }}
      />
    );
  }
  return (
    <Box
      role="img"
      aria-label={alt}
      sx={{
        width: size,
        height: size,
        borderRadius: `${tokens.radius.control}px`,
        bgcolor: tokens.brand,
        color: tokens.brandInk,
        display: 'grid',
        placeItems: 'center',
        fontWeight: 700,
        fontSize: size / 2,
        lineHeight: 1,
      }}
    >
      {productShortName.trim().charAt(0).toUpperCase()}
    </Box>
  );
}

interface BrandLogoProps {
  /** Dark mode, or sitting on a `brand` / `appBar` / `brandDeep` fill. */
  reversed?: boolean;
  /** What renders when the brand has no usable logo. */
  fallback?: 'wordmark' | 'mark';
  height?: number;
  /** Typography for the wordmark (it inherits the surrounding colour). */
  wordmarkSx?: object;
}

export function BrandLogo({
  reversed = false,
  fallback = 'wordmark',
  height = 36,
  wordmarkSx,
}: BrandLogoProps) {
  const { assets, productName, productShortName, lightSurface } = useBrand();
  const tokens = resolveTokens(useTheme());
  const { usable, onError } = useFailedImages();

  const image = (src: string): ReactNode => (
    <Box
      component="img"
      src={src}
      alt={productName}
      onError={onError(src)}
      sx={{ height, width: 'auto', display: 'block' }}
    />
  );

  if (reversed && usable(assets.logoReversed)) return image(assets.logoReversed);
  if (usable(assets.logo)) {
    if (!reversed) return image(assets.logo);
    return (
      <Box
        sx={{
          bgcolor: lightSurface,
          borderRadius: `${tokens.radius.control}px`,
          paddingInline: 0.75,
          paddingBlock: 0.25,
          display: 'inline-flex',
        }}
      >
        {image(assets.logo)}
      </Box>
    );
  }
  if (fallback === 'mark') return <BrandMark size={height} />;
  return (
    <Typography variant="mono" component="span" noWrap sx={{ color: 'inherit', ...wordmarkSx }}>
      {productShortName}
    </Typography>
  );
}
